import { useEffect, useState } from "react";
import { Room, Player, Round, Submission } from "@/hooks/useRoom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Check, Pencil, RotateCcw } from "lucide-react";
import RoomHeader from "@/components/RoomHeader";

interface Props {
  room: Room;
  players: Player[];
  round: Round;
  submissions: Submission[];
  me: Player;
  amHost: boolean;
}

const CARDS = ["1", "2", "3", "5", "8", "13", "21", "?", "☕"];

export default function PokerRound({ room, players, round, submissions, me, amHost }: Props) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(round.prompt);

  useEffect(() => {
    setTitleDraft(round.prompt);
  }, [round.prompt]);

  const mySub = submissions.find((s) => s.player_id === me.id);
  const allSubmitted = players.length > 0 && submissions.length >= players.length;

  const pickCard = async (value: string) => {
    if (mySub) {
      await supabase.from("submissions").update({ value }).eq("id", mySub.id);
    } else {
      await supabase.from("submissions").insert({ round_id: round.id, player_id: me.id, value });
    }
  };

  const reveal = async () => {
    await supabase.from("rounds").update({ status: "revealed" }).eq("id", round.id);
    await supabase.from("rooms").update({ status: "revealing" }).eq("id", room.id);
  };

  const saveTitle = async () => {
    await supabase.from("rounds").update({ prompt: titleDraft.trim() || round.prompt }).eq("id", round.id);
    setEditingTitle(false);
  };

  const resetRound = async () => {
    await supabase.from("submissions").delete().eq("round_id", round.id);
    setEditingTitle(true);
  };

  return (
    <main className="min-h-screen px-4 pb-6 flex flex-col">
      <RoomHeader room={room} me={me} amHost={amHost} players={players} />
      <div className="max-w-2xl mx-auto w-full space-y-5 flex-1 animate-pop-in pt-2">
        <div className="text-center text-xs font-semibold text-muted-foreground">
          STORY #{round.round_number}
        </div>
        <Card className="p-5 border-2 shadow-card">
          {editingTitle && amHost ? (
            <div className="flex gap-2">
              <Input
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                className="h-11 text-base font-semibold"
                autoFocus
                onKeyDown={(e) => e.key === "Enter" && saveTitle()}
              />
              <Button onClick={saveTitle} size="sm">Save</Button>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-xl font-extrabold flex-1">{round.prompt}</h2>
              {amHost && (
                <button onClick={() => setEditingTitle(true)} className="text-muted-foreground hover:text-primary">
                  <Pencil className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
        </Card>

        <Card className="p-4 border-2 shadow-card">
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">
            Voters ({submissions.length}/{players.length})
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {players.map((p) => {
              const voted = submissions.some((s) => s.player_id === p.id);
              return (
                <div
                  key={p.id}
                  className={`flex items-center gap-2 rounded-xl border-2 px-3 py-2 text-sm font-semibold ${
                    voted ? "border-success bg-success/10" : "border-border"
                  }`}
                >
                  <span className="flex-1 truncate">{p.name}</span>
                  {voted ? (
                    <Check className="h-4 w-4 text-success" strokeWidth={3} />
                  ) : (
                    <span className="text-xs text-muted-foreground">…</span>
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        {amHost && (
          <div className="flex gap-2">
            <Button
              onClick={resetRound}
              variant="outline"
              size="lg"
              className="h-14 px-4 font-bold"
              disabled={submissions.length === 0 && !round.prompt}
              title="Clear votes & edit story"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
            <Button
              onClick={reveal}
              disabled={submissions.length === 0}
              size="lg"
              className="flex-1 h-14 font-bold gradient-fun shadow-pop"
            >
              {allSubmitted ? "Reveal Votes" : `Reveal Now (${submissions.length}/${players.length})`}
            </Button>
          </div>
        )}
      </div>

      <div className="sticky bottom-0 -mx-4 px-4 pt-4 pb-6 bg-gradient-to-t from-background via-background to-transparent">
        <div className="max-w-2xl mx-auto">
          <div className="text-xs font-bold text-center text-muted-foreground mb-2">
            {mySub ? `You picked: ${mySub.value}` : "Pick a card"}
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
            {CARDS.map((c) => {
              const selected = mySub?.value === c;
              return (
                <button
                  key={c}
                  onClick={() => pickCard(c)}
                  className={`shrink-0 snap-center h-24 w-16 rounded-2xl border-2 text-2xl font-extrabold transition-bounce ${
                    selected
                      ? "gradient-primary text-white border-transparent shadow-pop scale-110 -translate-y-1"
                      : "bg-card border-border hover:border-primary hover:-translate-y-0.5 shadow-card"
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </main>
  );
}
